import { useMemo, useState, type ReactNode } from 'react';
import { t } from 'i18next';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
    RadixButton,
    RadixCard,
    RadixSpinner,
    RadixSwitch,
    RadixTable,
    RadixDropdown,
    RadixDialog,
    RadixTooltip,
} from '@/components/radix';
import type { ColumnType, RowType } from '@/components/radix/RadixTable';
import { useGetUnits } from '@/hooks/api/pos/units/useUnits';
import { useProductDetails } from '@/hooks/api/pos/products/useProducts';
import {
    useCreateServiceFormula,
    useDeleteServiceFormula,
    useGetServiceFormulas,
    useUpdateServiceFormula,
} from '@/hooks/api/services/formulas/useServiceFormulas';
import { useMediaQuery } from '@/hooks/shared';
import { MoreVertical } from 'lucide-react';
import ServiceFormulaUpsertModal from './ServiceFormulaUpsertModal';
import type { ServiceFormulaModalValues } from './types/index';
import {
    GetApiServicesServiceIdFormulas200FormulasItem,
    GetApiServicesServiceIdFormulas200FormulasItemProductAnyOfAnyOf,
    GetApiServicesServiceIdFormulas200FormulasItemUnitAnyOfAnyOf,
} from '@/shared/api/models';
import { getApiMessage } from '@/utils/getApiMessage';

export default function ServiceFormulaBuilder() {
    const { serviceId } = useParams();
    const isDesktop = useMediaQuery('(min-width: 960px)');

    const { data: units, isLoading: isLoadingUnits } = useGetUnits();

    const { data: formulasResponse, isLoading: isLoadingFormulas } = useGetServiceFormulas(
        String(serviceId ?? ''),
        !!serviceId,
    );

    const formulaRows = useMemo(() => formulasResponse?.formulas ?? [], [formulasResponse?.formulas]);
    const tableData: RowType[] = useMemo(() => formulaRows.map((f) => ({ id: f.id, formula: f })), [formulaRows]);

    const createFormula = useCreateServiceFormula(String(serviceId ?? ''));
    const updateFormula = useUpdateServiceFormula(String(serviceId ?? ''));
    const deleteFormula = useDeleteServiceFormula(String(serviceId ?? ''));

    const setTrackInventoryEnabled = async (enabled: boolean) => {
        const firstFormula = formulaRows[0];

        if (!firstFormula || !firstFormula.id) {
            toast.error(t('Services.NoFormulaAddErrMsg'));
            return;
        }

        await toast.promise(
            updateFormula.mutateAsync({
                formulaId: String(firstFormula.id),
                input: {
                    productId: String(firstFormula.productId),
                    consumeQty: Number(firstFormula.consumeQty),
                    isActive: true,
                    trackInventoryConsumption: enabled,
                },
            }),
            {
                loading: t('Services.InventoryFormulasToastUpdatingTrack'),
                success: t('Services.InventoryFormulasToastTrackUpdated'),
                error: (err: unknown) => getApiMessage(err) ?? t('Services.InventoryFormulasToastTrackUpdateError'),
            },
        );
    };

    const unitsById = useMemo(() => {
        const map: Record<string, { abbreviation?: string; name?: string }> = {};
        (units ?? []).forEach((u: any) => {
            map[String(u.id)] = { abbreviation: u.abbreviation, name: u.name };
        });
        return map;
    }, [units]);

    const anyMutationBusy =
        createFormula.isPending ||
        updateFormula.isPending ||
        deleteFormula.isPending ||
        isLoadingUnits ||
        isLoadingFormulas;

    const [upsertOpen, setUpsertOpen] = useState(false);
    const [upsertMode, setUpsertMode] = useState<'create' | 'edit'>('create');
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    /** Row selected for edit (upsert) or delete (confirm dialog); cleared when that flow closes. */
    const [selectedFormula, setSelectedFormula] = useState<GetApiServicesServiceIdFormulas200FormulasItem | null>(null);

    const openCreate = () => {
        setUpsertMode('create');
        setSelectedFormula(null);
        setUpsertOpen(true);
    };

    const openEdit = (formula: GetApiServicesServiceIdFormulas200FormulasItem) => {
        setUpsertMode('edit');
        setSelectedFormula(formula);
        setUpsertOpen(true);
    };

    const openDelete = (formula: GetApiServicesServiceIdFormulas200FormulasItem) => {
        setSelectedFormula(formula);
        setDeleteConfirmOpen(true);
    };

    const submitUpsert = async (values: ServiceFormulaModalValues) => {
        const qty = Number(values.consumeQty);
        const toastOptions = {
            loading: t('Services.InventoryFormulasToastSaving'),
            success: t('Services.InventoryFormulasToastSaved'),
            error: (err: unknown) => getApiMessage(err) ?? t('Services.InventoryFormulasToastSaveError'),
        };
        if (upsertMode === 'create') {
            await toast.promise(
                createFormula.mutateAsync({
                    productId: values.productId,
                    consumeQty: qty,
                    isActive: true,
                    ...(formulaRows.length === 0 ? { trackInventoryConsumption: true } : {}),
                }),
                toastOptions,
            );
            return;
        }
        if (!selectedFormula?.id) return;
        await toast.promise(
            updateFormula.mutateAsync({
                formulaId: String(selectedFormula.id),
                input: {
                    productId: values.productId,
                    consumeQty: qty,
                    isActive: true,
                },
            }),
            toastOptions,
        );
    };

    const confirmDelete = async () => {
        if (!selectedFormula?.id) return;
        const formulaId = String(selectedFormula.id);
        const isLastRow = formulaRows.length === 1;

        await toast.promise(
            (async () => {
                if (isLastRow) {
                    await updateFormula.mutateAsync({
                        formulaId,
                        input: {
                            productId: String(selectedFormula.productId ?? ''),
                            consumeQty: Number(selectedFormula.consumeQty),
                            isActive: Boolean(selectedFormula.isActive),
                            trackInventoryConsumption: false,
                        },
                    });
                }
                await deleteFormula.mutateAsync(formulaId);
            })(),
            {
                loading: t('Services.InventoryFormulasToastDeleting'),
                success: t('Services.InventoryFormulasToastDeleted'),
                error: (err: unknown) => getApiMessage(err) ?? t('Services.InventoryFormulasToastDeleteError'),
            },
        );
        setDeleteConfirmOpen(false);
        setSelectedFormula(null);
    };

    const QtyCell = ({ productId, consumeQty }: { productId: string | null; consumeQty: number }) => {
        const productDetails = useProductDetails(productId ?? '', !!productId);
        const totalBaseQuantity = (productDetails.data as any)?.totalBaseQuantity as number | undefined;
        const showQtyWarn =
            !!productId &&
            Number.isFinite(consumeQty) &&
            typeof totalBaseQuantity === 'number' &&
            totalBaseQuantity >= 0 &&
            consumeQty > totalBaseQuantity;

        return (
            <div className="space-y-1">
                <div>{consumeQty}</div>
                {showQtyWarn && (
                    <RadixTooltip title={t('Services.InventoryFormulasQtyWarn')}>
                        <div className="text-xs text-warning-600">{t('Services.InventoryFormulasQtyWarnShort')}</div>
                    </RadixTooltip>
                )}
            </div>
        );
    };

    const columns: ColumnType[] = useMemo(
        () => [
            {
                id: 'item',
                name: t('Services.InventoryFormulasColItem'),
                selector: (row) => row.formula.product?.name,
                width: '340px',
            },
            {
                id: 'qty',
                name: t('Services.InventoryFormulasColQty'),
                selector: (row) => (
                    <QtyCell
                        productId={row.formula?.productId ?? null}
                        consumeQty={Number(row.formula?.consumeQty ?? 0)}
                    />
                ),
                width: '120px',
            },
            {
                id: 'unit',
                name: t('Services.InventoryFormulasColUnit'),
                selector: (row) => row.formula.unit?.abbreviation ?? t('Common.DefaultProductUnit'),
                width: '220px',
            },
            {
                id: 'actions',
                name: t('Services.InventoryFormulasColActions'),
                selector: (row) => (
                    <RadixDropdown
                        align="end"
                        trigger={
                            <button
                                type="button"
                                className="w-9 h-9 inline-flex items-center justify-center rounded-md border-none bg-background-paper hover:bg-background-secondary"
                                aria-label={t('Services.InventoryFormulasActions')}
                            >
                                <MoreVertical className="w-4 h-4 text-text-secondary" />
                            </button>
                        }
                        items={[
                            {
                                label: t('Common.Edit'),
                                onClick: () => openEdit(row.formula),
                            },
                            { separator: true },
                            {
                                label: t('Common.Delete'),
                                onClick: () => openDelete(row.formula),
                            },
                        ]}
                    />
                ),
                width: '160px',
            },
        ],
        // eslint-disable-next-line react-hooks/exhaustive-deps -- t() for column titles; RadixTable reads row data from `data`
        [anyMutationBusy],
    );

    const trackInventoryEnabled = Boolean(formulasResponse?.trackInventoryConsumption);
    const hasFormulaRows = formulaRows.length > 0;

    const trackOffMessage = (
        <span className="text-sm text-text-secondary block text-center px-2 max-w-md mx-auto">
            {t('Services.InventorySwitchOffMsg')}
        </span>
    );

    /** Mobile: one card, ~same footprint as a formula row card; content centered. */
    const mobilePlaceholderCardClassName =
        'border border-solid border-border-default rounded-lg p-3 min-h-[8rem] flex items-center justify-center text-center';

    function renderInventoryFormulasList(): ReactNode {
        if (isLoadingFormulas) {
            return isDesktop ? (
                <RadixTable columns={columns} data={[]} loading NoDataText="" />
            ) : (
                <div className="md:p-3">
                    <RadixCard className={mobilePlaceholderCardClassName}>
                        <div className="flex items-center justify-center gap-3 text-sm text-text-secondary">
                            <RadixSpinner size="sm" />
                            {t('Common.Loading')}
                        </div>
                    </RadixCard>
                </div>
            );
        }

        if (!hasFormulaRows) {
            return isDesktop ? (
                <RadixTable columns={columns} data={[]} loading={false} NoDataText={t('Components.NoData')} />
            ) : (
                <div className="md:p-3">
                    <RadixCard className={mobilePlaceholderCardClassName}>
                        <p className="text-sm text-text-secondary m-0 px-2">{t('Components.NoData')}</p>
                    </RadixCard>
                </div>
            );
        }

        if (!trackInventoryEnabled) {
            return isDesktop ? (
                <RadixTable columns={columns} data={[]} loading={false} NoDataText={trackOffMessage} />
            ) : (
                <div className="md:p-3">
                    <RadixCard className={mobilePlaceholderCardClassName}>{trackOffMessage}</RadixCard>
                </div>
            );
        }

        return isDesktop ? (
            <RadixTable columns={columns} data={tableData} loading={false} NoDataText={t('Components.NoData')} />
        ) : (
            <div className="md:p-3 space-y-3">
                {formulaRows.map((f: GetApiServicesServiceIdFormulas200FormulasItem) => (
                    <RadixCard key={f.id} className="border border-solid border-border-default rounded-lg p-3">
                        <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0 w-full">
                                <p className="text-text-primary font-medium line-clamp-1 m-0 p-0">
                                    {
                                        (f.product as GetApiServicesServiceIdFormulas200FormulasItemProductAnyOfAnyOf)
                                            ?.name
                                    }
                                </p>
                                <div className="mt-4 flex flex-col items-start gap-2 text-xs text-text-secondary">
                                    <div>
                                        <div className="font-medium text-text-primary">
                                            {t('Services.InventoryFormulasColQty')}
                                        </div>
                                        <QtyCell
                                            productId={f.productId ?? null}
                                            consumeQty={Number(f.consumeQty ?? 0)}
                                        />
                                    </div>
                                    <div>
                                        <div className="font-medium text-text-primary">
                                            {t('Services.InventoryFormulasColUnit')}
                                        </div>
                                        <div>
                                            {(f.unit as GetApiServicesServiceIdFormulas200FormulasItemUnitAnyOfAnyOf)
                                                ?.abbreviation ?? t('Common.DefaultProductUnit')}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <RadixDropdown
                                align="end"
                                trigger={
                                    <button
                                        type="button"
                                        className="w-9 h-9 inline-flex items-center justify-center rounded-md border-none bg-background-paper hover:bg-background-secondary"
                                        aria-label={t('Services.InventoryFormulasActions')}
                                    >
                                        <MoreVertical className="w-4 h-4 text-text-secondary" />
                                    </button>
                                }
                                items={[
                                    { label: t('Common.Edit'), onClick: () => openEdit(f) },
                                    { separator: true },
                                    { label: t('Common.Delete'), onClick: () => openDelete(f) },
                                ]}
                            />
                        </div>
                    </RadixCard>
                ))}
            </div>
        );
    }

    return (
        <div className="px-2 md:px-6 py-6">
            <div className="flex flex-wrap items-center justify-between md:gap-3">
                <div className="flex items-center gap-2">
                    <h1 className="text-xl md:text-2xl font-semibold text-text-primary">
                        {t('Services.ServiceConsumption')}
                    </h1>
                </div>

                <div className="flex items-center gap-2 w-full md:w-auto">
                    <RadixButton
                        className="w-full md:w-auto capitalize"
                        onClick={openCreate}
                        disabled={anyMutationBusy}
                    >
                        {t('Services.AddProduct')}
                    </RadixButton>
                </div>
            </div>

            <div className="mt-3">
                <div className="flex items-center gap-2 md:gap-4">
                    <RadixSwitch
                        checked={formulaRows.length > 0 && (formulasResponse?.trackInventoryConsumption ?? false)}
                        onChange={(checked: boolean) => setTrackInventoryEnabled(Boolean(checked))}
                        disabled={anyMutationBusy}
                    />
                    <div>
                        <div className="text-sm font-medium text-text-primary">
                            {t('Services.EnableServiceConsumption')}
                        </div>
                        <div className="text-xs text-text-secondary">{t('Services.EnableServiceConsumptionHint')}</div>
                    </div>
                </div>
            </div>

            <div className="overflow-hidden mt-4">{renderInventoryFormulasList()}</div>

            <ServiceFormulaUpsertModal
                open={upsertOpen}
                onOpenChange={(open) => {
                    setUpsertOpen(open);
                    if (!open) setSelectedFormula(null);
                }}
                mode={upsertMode}
                unitsById={unitsById}
                isSubmittingExternal={anyMutationBusy}
                initialValues={{
                    productId: selectedFormula?.productId ? String(selectedFormula.productId) : '',
                    consumeQty: selectedFormula?.consumeQty != null ? String(selectedFormula.consumeQty) : '',
                    isActive: true,
                }}
                onSubmit={submitUpsert}
            />

            <RadixDialog
                open={deleteConfirmOpen}
                onOpenChange={(open) => {
                    setDeleteConfirmOpen(open);
                    if (!open) setSelectedFormula(null);
                }}
                title={t('Services.InventoryFormulasDeleteTitle')}
                footer={
                    <div className="flex items-center gap-2 w-full justify-end">
                        <RadixButton
                            variant="outline"
                            onClick={() => setDeleteConfirmOpen(false)}
                            disabled={anyMutationBusy}
                        >
                            {t('Setting.Cancel')}
                        </RadixButton>
                        <RadixButton onClick={confirmDelete} disabled={anyMutationBusy}>
                            {t('Common.Delete')}
                        </RadixButton>
                    </div>
                }
            >
                <div className="text-base text-text-secondary">
                    {t('Services.InventoryFormulasDeleteDescription', {
                        productName: (selectedFormula?.product as any)?.name ?? t('Common.N/A'),
                    })}
                </div>
            </RadixDialog>
        </div>
    );
}
